/* eslint-disable @typescript-eslint/no-explicit-any */
import store from "../store";
import { BaseUser } from "../store/types/common.types";
import { UsersTeam } from "../store/types/user.types";

type UserField = {
  Id: number;
  Name: string;
};

type UserFieldKeys = "CreatedBy" | "ChangedBy" | "AssignedTo" | "ClosedBy";
type UserIdKeys = "CreatedById" | "ChangedById" | "AssignedToId" | "ClosedById";
// Lazy load userApi only when needed

async function getUserApi() {
  const { userApi } = await import("../store/services/userService");
  return userApi;
}

const FIELD_MAPPINGS: Record<UserIdKeys, UserFieldKeys> = {
  CreatedById: "CreatedBy",
  ChangedById: "ChangedBy",
  AssignedToId: "AssignedTo",
  ClosedById: "ClosedBy",
} as const;

export async function mapExternalUsers(
  item: any,
  preloadedState?: ReturnType<typeof store.getState>,
  prefetchUsers: boolean = true
): Promise<any> {
  if (!item) return item;

  // If this is the top-level call, fetch all users first
  if (prefetchUsers) {
    const allUserIds = findUserIds(item);
    await lazyFetchExternalUsers(allUserIds);
    // Get fresh state after fetching users
    preloadedState = store.getState();
  }

  const state = preloadedState || store.getState();

  // Create a deep clone to ensure mutability
  const result = JSON.parse(JSON.stringify(item));

  for (const [key, value] of Object.entries(item)) {
    if (Array.isArray(value)) {
      result[key] = await Promise.all(
        value.map((item) =>
          typeof item === "object" ? mapExternalUsers(item, state, false) : item
        )
      );
    } else if (value && typeof value === "object") {
      result[key] = await mapExternalUsers(value, state, false);
    }
  }

  // Map user fields at current level
  for (const [idKey, userKey] of Object.entries(FIELD_MAPPINGS)) {
    if (idKey in item) {
      const userId = item[idKey] as number;
      if (userId && userId !== 0) {
        const user = (await getUserApi()).endpoints.getExternalUser.select(
          userId
        )(state)?.data;
        result[userKey] = {
          Id: user?.Id ?? 0,
          Name:
            user?.Name ?? (userKey === "AssignedTo" ? "Ingen ansvarig" : ""),
        } as UserField;
      }
    } else {
      result[userKey] = null;
    }
  }

  return result;
}

// Helper function to map arrays of items
export async function mapExternalUsersArray<T extends Record<string, any>>(
  items: T[]
): Promise<T[]> {
  if (!items?.length) return [];

  // First, find all user IDs in all items
  const allUserIds = findUserIds(items);

  // Fetch any uncached users once for all items
  await lazyFetchExternalUsers(allUserIds);

  // Get state once after fetching all users
  const state = store.getState();

  // Map each item using the same state
  return Promise.all(items.map((item) => mapExternalUsers(item, state, false)));
}

function findUserIds(obj: any): number[] {
  if (!obj) return [];

  // Get keys from our type-safe FIELD_MAPPINGS constant
  const targetFields = Object.keys(FIELD_MAPPINGS) as UserIdKeys[];
  let userIds: number[] = [];

  if (Array.isArray(obj)) {
    // If it's an array, recursively process each element
    obj.forEach((item) => {
      userIds = [...userIds, ...findUserIds(item)];
    });
  } else if (typeof obj === "object") {
    // Process object properties
    Object.entries(obj).forEach(([key, value]) => {
      if (
        targetFields.includes(key as UserIdKeys) &&
        typeof value === "number" &&
        value !== 0
      ) {
        userIds.push(value);
      } else if (value && typeof value === "object") {
        // Recursively process nested objects/arrays
        userIds = [...userIds, ...findUserIds(value)];
      }
    });
  }

  // Remove duplicates, nulls, undefined, and 0s
  return Array.from(new Set(userIds)).filter(
    (id) => id !== null && id !== undefined && id !== 0
  );
}

export async function lazyFetchExternalUsers(
  userIds: number[],
  dispatch = store.dispatch
): Promise<BaseUser[]> {
  if (userIds.length === 0) {
    return [];
  }
  const state = store.getState();
  const uncachedUserIds = userIds.filter(async (id) => {
    const queryState = (await getUserApi()).endpoints.getExternalUser.select(
      id
    )(state);
    return !queryState.data; // Only include IDs that don't have data in cache
  });

  if (!uncachedUserIds || uncachedUserIds.length === 0) {
    return [];
  }

  // Use initiate directly instead of the hook
  const promise = dispatch(
    (await getUserApi()).endpoints.getExternalUsers.initiate(uncachedUserIds)
  );

  // Wait for the query to complete and cache to be updated
  const result = await promise;

  // Manually update individual user caches
  if (result.data) {
    await Promise.all(
      result.data.map(async (user) =>
        dispatch(
          (
            await getUserApi()
          ).util.upsertQueryData("getExternalUser", user.Id, user)
        )
      )
    );
  }

  return result.data || [];
}

export async function mapTeamsWithUsers(
  teams: UsersTeam[]
): Promise<UsersTeam[]> {
  if (!teams?.length) return [];

  // Collect all unique user IDs from all teams
  const allUserIds = Array.from(
    new Set(
      teams
        .flatMap((team) => team.UserIds.map((id) => id))
        .filter((id) => id !== 0)
    )
  );

  // Fetch all users in one batch
  await lazyFetchExternalUsers(allUserIds);

  // Get state once after fetching all users
  const state = store.getState();

  // Map all teams and their users using the cached data
  return Promise.all(
    teams.map(async (team) => ({
      ...team,
      Users: await Promise.all(
        team.UserIds.map(async (userId) => {
          const userData = (
            await getUserApi()
          ).endpoints.getExternalUser.select(userId)(state)?.data;
          return {
            Id: userId,
            Name: userData?.Name ?? "",
          };
        })
      ),
    }))
  );
}
